import React, { type ReactNode } from 'react';

export type SettingsRouteSectionId =
  | 'seity'
  | 'desk'
  | 'trust'
  | 'voice'
  | 'standards'
  | 'taste'
  | 'models'
  | 'providers'
  | 'keys'
  | 'routing'
  | 'spending'
  | 'local'
  | 'talents'
  | 'connectors'
  | 'memory'
  | 'notifications'
  | 'data'
  | 'billing'
  | 'costs'
  | 'agents'
  | 'runtime';

export type SettingsNavItem = {
  id: SettingsRouteSectionId;
  label: string;
  description: string;
  group: string;
};

export const SETTINGS_SECTION_ALIAS: Record<string, SettingsRouteSectionId> = {
  'alter-ego': 'seity',
  'data-mgmt': 'data',
  'data-privacy': 'data',
  advanced: 'standards',
  quality: 'standards',
  'machine-room': 'standards',
  skills: 'talents',
  sources: 'connectors',
  subscription: 'billing',
  // The LLM Cockpit consolidates the former stand-alone "providers" page into
  // the Models surface. Keep the alias so /settings/providers deep-links still
  // land somewhere useful (the cockpit at /settings/models).
  providers: 'models',
  // The old Costs panel left the menu with the Models & Routing section;
  // /settings/costs deep-links land on its replacement, Spending and usage.
  costs: 'spending',
};

/** Subset used by Makers workbench embedded settings (canonical panels only). */
export const WORKBENCH_SETTINGS_SECTIONS: SettingsRouteSectionId[] = [
  'seity',
  'models',
  'connectors',
  'voice',
  'talents',
  'standards',
  'notifications',
  'data',
  'billing',
];

export const SETTINGS_NAV_GROUPS: Array<{ title: string; items: SettingsNavItem[] }> = [
  {
    title: 'Identity & Bond',
    items: [
      {
        id: 'seity',
        label: 'Seity',
        description: 'Name, interaction style, autonomy posture.',
        group: 'Identity & Bond',
      },
      {
        id: 'trust',
        label: 'Bond & Trust',
        description: 'Read-only trust signals and bond posture.',
        group: 'Identity & Bond',
      },
    ],
  },
  {
    title: 'Writing',
    items: [
      {
        id: 'desk',
        label: 'Writing desk',
        description: 'Declarations, autosave, provenance view, and new-project defaults.',
        group: 'Writing',
      },
    ],
  },
  {
    title: 'Voice & Canon',
    items: [
      {
        id: 'voice',
        label: 'Voice',
        description: 'Calibration strategy and drift tolerances.',
        group: 'Voice & Canon',
      },
      {
        id: 'taste',
        label: 'Canon',
        description: 'Canon Learning + Canon Code calibration.',
        group: 'Voice & Canon',
      },
      {
        id: 'standards',
        label: 'Governor',
        description: 'Quality enforcement — N1–N5 strictness, ORA loop, Canon tie-in, kill switches.',
        group: 'Voice & Canon',
      },
    ],
  },
  {
    title: 'Models & Routing',
    items: [
      {
        id: 'models',
        label: 'Models',
        description: 'What exists, data terms, and the tick to enable — all read from the engine.',
        group: 'Models & Routing',
      },
      {
        id: 'keys',
        label: 'Keys and accounts',
        description: 'Your own keys, subscriptions and aggregators, with Test and data terms beside each key.',
        group: 'Models & Routing',
      },
      {
        id: 'routing',
        label: 'Routing',
        description: 'Task, then model: mappings, pins, allowed providers, fallback order.',
        group: 'Models & Routing',
      },
      {
        id: 'spending',
        label: 'Spending and usage',
        description: 'Spend limits per provider, and use today and this week from usage records.',
        group: 'Models & Routing',
      },
      {
        id: 'local',
        label: 'Local and advanced',
        description: 'Local models, custom credentials, and diagnostics (operator-only).',
        group: 'Models & Routing',
      },
    ],
  },
  {
    title: 'Talents & Famili',
    items: [
      {
        id: 'talents',
        label: 'Talents',
        description: 'Installed capabilities and future famili controls.',
        group: 'Talents & Famili',
      },
      {
        id: 'connectors',
        label: 'Connectors',
        description: 'External source connections and sync surfaces.',
        group: 'Talents & Famili',
      },
    ],
  },
  {
    title: 'Memory & Account',
    items: [
      {
        id: 'memory',
        label: 'Memory',
        description: 'Mirror readiness, growth edges, Codex legibility.',
        group: 'Memory & Account',
      },
      {
        id: 'notifications',
        label: 'Notifications',
        description: 'Live delivery preferences and quiet hours.',
        group: 'Memory & Account',
      },
      {
        id: 'data',
        label: 'Data & Privacy',
        description: 'Exports, retention, restore, and danger zone.',
        group: 'Memory & Account',
      },
      {
        id: 'billing',
        label: 'Billing',
        description: 'Dedicated subscription and credits route.',
        group: 'Memory & Account',
      },
    ],
  },
  {
    title: 'System',
    items: [
      {
        id: 'agents',
        label: 'Agents',
        description: 'Harness health, scheduled jobs, dispatch runtime.',
        group: 'System',
      },
      {
        id: 'runtime',
        label: 'Runtime',
        description: 'Connection status, backend version, database info.',
        group: 'System',
      },
    ],
  },
];

export const ALL_SETTINGS_NAV_ITEMS = SETTINGS_NAV_GROUPS.flatMap((g) => g.items);

export function normalizeSettingsRouteSection(value: string | undefined): SettingsRouteSectionId {
  if (!value) return 'seity';
  if (SETTINGS_SECTION_ALIAS[value]) return SETTINGS_SECTION_ALIAS[value];
  return (ALL_SETTINGS_NAV_ITEMS.find((item) => item.id === value)?.id ?? 'seity') as SettingsRouteSectionId;
}

/** Map modal / legacy panel ids to URL section segments for /settings/:sectionId */
export function settingsModalPanelIdToPath(panelId: string): string {
  const map: Record<string, string> = {
    'alter-ego': '',
    advanced: 'standards',
    'machine-room': 'standards',
    quality: 'standards',
    'data-mgmt': 'data',
    skills: 'talents',
    sources: 'connectors',
    subscription: 'billing',
  };
  if (panelId === 'alter-ego') return '/settings';
  const segment = map[panelId] ?? panelId;
  return `/settings/${segment}`;
}

export function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const PanelWrapper: React.FC<{ title: string; description: string; children: ReactNode }> = ({
  title,
  description,
  children,
}) =>
  React.createElement(
    'div',
    { className: 'space-y-4' },
    React.createElement(
      'div',
      { className: 'rounded-xl border p-4', style: { borderColor: 'var(--border)' } },
      React.createElement(
        'div',
        { className: 'text-sm font-semibold', style: { color: 'var(--text-primary)' } },
        title,
      ),
      React.createElement(
        'div',
        { className: 'mt-1 text-sm leading-6', style: { color: 'var(--text-secondary)' } },
        description,
      ),
    ),
    children,
  );

export const LinkedSurfacePanel: React.FC<{
  title: string;
  description: string;
  ctaLabel: string;
  onOpen: () => void;
  footer?: ReactNode;
}> = ({ title, description, ctaLabel, onOpen, footer }) =>
  React.createElement(
    'div',
    { className: 'space-y-4' },
    React.createElement(
      'div',
      { className: 'rounded-xl border p-6', style: { borderColor: 'var(--border)' } },
      React.createElement(
        'div',
        { className: 'text-lg font-semibold', style: { color: 'var(--text-primary)' } },
        title,
      ),
      React.createElement(
        'div',
        {
          className: 'mt-2 max-w-2xl text-sm leading-6',
          style: { color: 'var(--text-secondary)' },
        },
        description,
      ),
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: onOpen,
          className: 'mt-4 rounded-lg px-4 py-2 text-sm font-medium',
          style: { background: 'var(--accent-gold)', color: 'var(--bg-primary)' },
        },
        ctaLabel,
      ),
    ),
    footer,
  );
